<template>
	<button
		:class="btnClasses"
		type="button"
		:disabled
		:aria-label="label ?? 'button'"
	>
		<slot name="icon-left">
			<UIShIcon v-if="iconLeft" :name="iconLeft" v-bind="iconProps" />
		</slot>

		<slot :value="label">
			<span v-if="label">{{ label }}</span>
		</slot>

		<slot name="icon-right">
			<UIShIcon v-if="iconRight" :name="iconRight" v-bind="iconProps" />
		</slot>
	</button>
</template>

<script setup lang="ts">
const BUTTON_SIZES = ['sm', 'md', 'lg', 'xl'] as const;

type ShButtonSizesT = 'sm' | 'md' | 'lg' | 'xl';
type ShBtnSizeName = `btn-${(typeof BUTTON_SIZES)[number]}`;
type ShBtnSizeNameAndValue = `btn-${(typeof BUTTON_SIZES)[number]}${number}`;
type UISizeBckPoint = ShBtnSizeName | ShBtnSizeNameAndValue;
type ShBaseButtonT = {
	label?: string;
	iconLeft?: string;
	iconRight?: string;
	disabled?: boolean;
	fill?: boolean;
	kind?:
		| 'primary'
		| 'secondary-gray'
		| 'secondary-color'
		| 'tertiary-gray'
		| 'unstyled';
};

export type ShButtonT = ShBaseButtonT & {
	[s in ShButtonSizesT]?: number | boolean;
};

const props = withDefaults(defineProps<ShButtonT>(), {
	kind: 'primary'
});

const iconProps = computed(() => {
	return {
		class: 'btn-icon',
		size: 'sm' in props ? '1.111rem' : '1.333rem'
	};
});

const btnClasses = computed(() => {
	if (props.kind === 'unstyled') return {};

	const sizeClass = getSizeClass();

	return {
		btn: true,
		'btn-fill': props.fill,
		'btn-disabled': props.disabled,
		[`btn-${props.kind}`]: !props.disabled,
		...sizeClass
	};
});

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	BUTTON_SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`btn-${s}`] = value);
		}

		classes[`btn-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style scoped lang="scss">
.btn {
	display: flex;
	cursor: pointer;
	overflow: hidden;
	align-items: center;
	justify-content: center;

	width: fit-content;
	height: auto;
	border-radius: 32.004px;
	border-width: 0.056rem;
	border-style: solid;

	&:focus-visible,
	&:active {
		box-shadow:
			0rem 0rem 0rem 0.111rem var(--color-white),
			0rem 0rem 0rem 0.222rem var(--color-brand500);
	}

	&:disabled {
		user-select: none;
		pointer-events: none;
		color: var(--color-gray400);
		border-color: var(--color-gray200);
		background-color: var(--color-gray100);

		&:focus-visible,
		&:active {
			box-shadow: none;
		}
	}

	& .btn-icon {
		color: inherit;
	}
}

.btn-primary {
	color: var(--color-white);
	border-color: var(--color-brand500);
	background-color: var(--color-brand500);

	&:hover {
		border-color: var(--color-brand600);
		background-color: var(--color-brand600);
	}

	&:focus-visible,
	&:active {
		border-color: var(--color-brand500);
		background-color: var(--color-brand500);
	}
}

.btn-secondary-gray {
	color: var(--color-white);
	border-color: var(--color-gray900);
	background-color: var(--color-gray900);

	&:hover {
		border-color: var(--color-brand600);
		background-color: var(--color-brand600);
	}

	&:focus-visible,
	&:active {
		border-color: var(--color-gray900);
		background-color: var(--color-gray900);
	}
}

.btn-secondary-color {
	color: var(--color-brand500);
	border-color: var(--color-brand500);
	background-color: var(--color-white);

	&:hover {
		color: var(--color-white);
		border-color: var(--color-brand600);
		background-color: var(--color-brand600);
	}

	&:focus-visible,
	&:active {
		color: var(--color-brand500);
		background-color: var(--color-white);
	}
}

.btn-tertiary-gray {
	border-radius: 0.889rem;
	color: var(--color-gray950);
	border-color: var(--color-gray100);
	background-color: var(--color-gray100);

	&:hover {
		border-color: var(--color-brand100);
		background-color: var(--color-brand100);
	}

	&:focus-visible,
	&:active {
		border-color: var(--color-gray100);
		background-color: var(--color-gray100);
	}

	& .btn-icon {
		color: var(--color-brand500);
	}
}

.btn-disabled {
	user-select: none;
	pointer-events: none;
	color: var(--color-gray400);
	border-color: var(--color-gray200);
	background-color: var(--color-gray100);

	&:focus-visible,
	&:active {
		box-shadow: none;
	}
}

.btn-fill {
	width: 100%;
}

$sizes: (
	sm: tsmb,
	md: tmdb,
	lg: tlgb,
	xl: tlgb
);
$screens: 768, 1024, 1240, 1440, 1920;

@mixin useBtn($size, $font) {
	gap: var(--btn-#{$size}-gap);
	padding: var(--btn-#{$size}-padding);

	@include useFont($font);

	.btn-icon {
		--icon-width: var(--btn-#{$size}-icon-w);
		--icon-height: var(--btn-#{$size}-icon-h);
	}
}

@each $size, $font in $sizes {
	.btn-#{$size} {
		@include useBtn($size, $font);
	}
}

@each $screen in $screens {
	@each $size, $font in $sizes {
		.btn-#{$size}#{$screen} {
			@media screen and (min-width: #{$screen}px) {
				@include useBtn($size, $font);
			}
		}
	}
}
</style>
